// Build timestamp: 2025-07-16 11:25:00
import { db, storage, functions, auth, getFieldValue } from './firebase-config.js';
import { cleanForFirestore } from './utils.js';

const ApplicationSystem = {
    async saveDraft(userId, draftData) {
        try {
            if (!userId) {
                throw new Error('User must be authenticated to save draft');
            }
            
            const draft = {
                ...draftData,
                userId: userId,
                lastSaved: getFieldValue().serverTimestamp()
            };
            
            // Sanitize draft data before saving
            const sanitizedDraft = cleanForFirestore(draft);
            
            await db.collection('applicationDrafts').doc(userId).set(sanitizedDraft);
            
            return { success: true };
        } catch (error) {
            console.error('Error saving draft:', error);
            return { success: false, error: error.message };
        }
    },
    
    async getDraft(userId) {
        try {
            if (!userId) {
                throw new Error('User must be authenticated to get draft');
            }
            
            const doc = await db.collection('applicationDrafts').doc(userId).get();
            
            if (!doc.exists) {
                return { success: true, draft: null };
            }
            
            return { 
                success: true, 
                draft: {
                    id: doc.id,
                    ...doc.data()
                }
            };
        } catch (error) {
            console.error('Error getting draft:', error);
            return { success: false, error: error.message };
        }
    },
    
    async deleteDraft(userId) {
        try {
            if (!userId) {
                throw new Error('User must be authenticated to delete draft');
            }
            
            await db.collection('applicationDrafts').doc(userId).delete();
            
            return { success: true };
        } catch (error) {
            console.error('Error deleting draft:', error);
            return { success: false, error: error.message };
        }
    },
    async submitApplication(applicationData, files = {}) {
        //console.log('form sumbsiion');
        try {
            // Check server-side rate limit first
            const submitApplicationFn = functions.httpsCallable('submitApplication');
            const rateLimitResult = await submitApplicationFn();
            
            if (!rateLimitResult.data.rateLimitPassed) {
                throw new Error('Rate limit exceeded. Please try again later.');
            }
            // Generate a unique application ID first
            const applicationId = db.collection('applications').doc().id;
            
            // Upload files first if any
            let uploadedFiles = {};
            if (Object.keys(files).length > 0) {
                uploadedFiles = await this.uploadMultipleFiles(files, applicationId);
            }
            
            // Create the serverTimestamp field value
            let serverTimestamp;
            try {
                serverTimestamp = getFieldValue().serverTimestamp();
                console.log('ServerTimestamp created:', serverTimestamp);
            } catch (e) {
                console.error('Failed to create serverTimestamp:', e);
                serverTimestamp = new Date(); // Fallback to regular Date
            }
            
            // Create application document with all required fields and file URLs
            const application = {
                ...applicationData,
                files: uploadedFiles, // Include uploaded file URLs
                status: 'submitted', // Create with final status
                submittedAt: serverTimestamp,
                reviewedBy: null,
                reviewedAt: null,
                reviewNotes: null
            };
            
            // Debug logging - log the raw application data
            console.log('Raw application data before sanitization:', JSON.stringify(application, (key, value) => {
                if (value === undefined) return '[UNDEFINED]';
                if (value === null) return '[NULL]';
                if (value && value._methodName) return `[FieldValue.${value._methodName}()]`;
                if (value instanceof File) return `[File: ${value.name}]`;
                if (value instanceof FileList) return `[FileList: ${value.length} files]`;
                if (typeof value === 'object' && value && value.constructor) {
                    const name = value.constructor.name;
                    if (name !== 'Object' && name !== 'Array') {
                        return `[${name} instance]`;
                    }
                }
                return value;
            }, 2));
            
            // Sanitize the application data to remove undefined values
            const sanitizedApplication = cleanForFirestore(application);
            
            // Debug logging - log the sanitized data
            console.log('Sanitized application data:', JSON.stringify(sanitizedApplication, (key, value) => {
                if (value === undefined) return '[UNDEFINED]';
                if (value === null) return '[NULL]';
                if (value && value._methodName) return `[FieldValue.${value._methodName}()]`;
                return value;
            }, 2));
            
            // Create the application document with the pre-generated ID
            try {
                await db.collection('applications').doc(applicationId).set(sanitizedApplication);
            } catch (firestoreError) {
                console.error('=== FIRESTORE WRITE ERROR ===');
                console.error('Full error object:', firestoreError);
                console.error('Error code:', firestoreError.code);
                console.error('Error message:', firestoreError.message);
                console.error('Error details:', firestoreError.details);
                
                // Log the application data for debugging
                console.error('Application ID:', applicationId);
                console.error('Application keys:', Object.keys(sanitizedApplication));
                
                // Try to identify problematic fields
                for (const [key, value] of Object.entries(sanitizedApplication)) {
                    if (value && typeof value === 'object') {
                        console.error(`Field "${key}" type:`, Object.prototype.toString.call(value));
                        if (value.constructor && value.constructor.name) {
                            console.error(`Field "${key}" constructor:`, value.constructor.name);
                        }
                    }
                }
                
                // Check for common issues
                if (firestoreError.message && firestoreError.message.includes('Invalid document')) {
                    console.error('This is likely due to an invalid field value (undefined, NaN, Infinity, or unsupported object type)');
                }
                
                throw new Error(`Failed to save application: ${firestoreError.message || 'Unknown error'}`);
            }
            
            // Delete draft if it exists
            if (applicationData.userId) {
                await this.deleteDraft(applicationData.userId);
            }
            
            return { success: true, applicationId: applicationId };
        } catch (error) {
            console.error('Error submitting application:', error);
            return { success: false, error: error.message };
        }
    },
    
    async getApplications(filter = 'all') {
        let query = db.collection('applications');
        
        if (filter !== 'all') {
            query = query.where('status', '==', filter);
        }
        
        const snapshot = await query.orderBy('submittedAt', 'desc').get();
        
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    },
    
    async getApplication(applicationId) {
        const doc = await db.collection('applications').doc(applicationId).get();
        
        if (!doc.exists) {
            throw new Error('Application not found');
        }
        
        return {
            id: doc.id,
            ...doc.data()
        };
    },
    
    async updateApplicationStatus(applicationId, status, adminId, notes = '') {
        try {
            // Version indicator for debugging
            console.log('ApplicationSystem.updateApplicationStatus - Version: 2025-07-16-11:35');
            console.log('Admin UID:', adminId);
            console.log('Application ID:', applicationId);
            console.log('Status:', status);
            
            // Pre-check and create user document if needed (outside transaction)
            if (status === 'approved') {
                const appDoc = await db.collection('applications').doc(applicationId).get();
                if (!appDoc.exists) {
                    throw new Error('Application not found');
                }
                
                const application = appDoc.data();
                const userRef = db.collection('users').doc(application.userId);
                const userDoc = await userRef.get();
                
                if (!userDoc.exists) {
                    console.log('Pre-creating user document for:', application.userId);
                    // Create user document outside of transaction
                    const userData = {
                        uid: application.userId,
                        email: application.basicInfo.email,
                        fullName: application.basicInfo.fullName,
                        isHelper: false, // Will be updated to true in transaction
                        karmaPoints: 0,
                        emailVerified: false,
                        isAdmin: false
                    };
                    
                    await userRef.set(userData);
                    console.log('User document created successfully');
                }
            }
            
            // Use a transaction to ensure atomicity
            await db.runTransaction(async (transaction) => {
                // PHASE 1: ALL READS (must be done before any writes)
                const appRef = db.collection('applications').doc(applicationId);
                const appDoc = await transaction.get(appRef);
                
                if (!appDoc.exists) {
                    throw new Error('Application not found');
                }
                
                const application = { id: appDoc.id, ...appDoc.data() };
                
                // PHASE 2: ALL WRITES (after all reads are complete)
                
                // Update application status
                transaction.update(appRef, {
                    status: status,
                    reviewedBy: adminId,
                    reviewedAt: getFieldValue().serverTimestamp(),
                    reviewNotes: notes
                });
                
                // If approved, update user and create helper profile
                if (status === 'approved') {
                    console.log('Processing approval - updating user and creating helper');
                    
                    // Update user document (it now exists for sure)
                    const userRef = db.collection('users').doc(application.userId);
                    console.log('Updating user document to set isHelper: true');
                    transaction.update(userRef, {
                        isHelper: true
                    });
                    
                    // Create helper profile
                    const helperRef = db.collection('helpers').doc(application.userId);
                    
                    // Extract and validate helper data
                    const rawBio = application.introduction?.writtenIntro || application.background?.writtenIntro || '';
                    console.log('Raw bio length:', rawBio.length);
                    console.log('Bio contains special chars:', /[''""–—]/.test(rawBio));
                    
                    // Clean the bio - replace smart quotes and other problematic characters
                    const cleanedBio = rawBio
                        .replace(/['']/g, "'")  // Replace smart quotes with regular quotes
                        .replace(/[""]/g, '"')  // Replace smart double quotes
                        .replace(/[–—]/g, '-')  // Replace em/en dashes with regular dashes
                        .trim();
                    
                    const helperData = {
                        userId: application.userId,
                        fullName: application.basicInfo.fullName,
                        karmaPoints: 0,
                        title: application.basicInfo.title || '',
                        expertiseAreas: application.expertise.areas || [],
                        bio: cleanedBio.substring(0, 1000), // Ensure bio doesn't exceed 1000 chars
                        certifications: application.expertise.certifications || null,
                        experience: application.basicInfo.experience || null,
                        linkedIn: application.basicInfo.linkedIn || null,
                        website: application.introduction?.website || application.background?.website || null,
                        calendlyUrl: application.introduction?.calendlyUrl || application.background?.calendlyUrl || null,
                        isActive: true,
                        status: 'approved',
                        approvedAt: getFieldValue().serverTimestamp(),
                        applicationId: application.id
                    };
                    
                    console.log('Creating helper document with cleaned bio length:', helperData.bio.length);
                    console.log('Helper data fields:', Object.keys(helperData));
                    
                    // Validate required fields
                    if (!helperData.userId || typeof helperData.userId !== 'string') {
                        throw new Error('Invalid userId for helper document');
                    }
                    if (typeof helperData.isActive !== 'boolean') {
                        throw new Error('Invalid isActive value for helper document');
                    }
                    
                    // Don't sanitize helper data - it contains FieldValue objects that must be preserved
                    transaction.set(helperRef, helperData);
                }
            });
            
            return { success: true };
        } catch (error) {
            console.error('Error updating application:', error);
            return { 
                success: false, 
                error: error.message,
                errorCode: error.code || 'unknown'
            };
        }
    },
    
    
    async uploadFile(file, type, applicationId) {
        try {
            // Get current user ID
            const userId = auth.currentUser?.uid;
            if (!userId) {
                throw new Error('User not authenticated');
            }
            
            // Create a unique file path that includes userId for ownership
            const timestamp = Date.now();
            const fileName = `${timestamp}_${file.name}`;
            const filePath = `applications/${userId}/${applicationId}/${type}/${fileName}`;
            
            // Create storage reference
            const storageRef = storage.ref().child(filePath);
            
            // Upload file
            const uploadTask = await storageRef.put(file);
            
            // Get download URL
            const downloadURL = await uploadTask.ref.getDownloadURL();
            
            const fileData = {
                name: file.name,
                type: file.type,
                size: file.size,
                url: downloadURL,
                path: filePath,
                uploadedAt: new Date().toISOString()
            };
            
            return {
                success: true,
                fileData: fileData
            };
        } catch (error) {
            console.error('Error uploading file:', error);
            return { success: false, error: error.message };
        }
    },
    
    async uploadMultipleFiles(files, applicationId) {
        const uploadedFiles = {};
        
        // Upload credentials (multiple files)
        if (files.credentials && files.credentials.length > 0) {
            uploadedFiles.credentials = [];
            for (let i = 0; i < files.credentials.length; i++) {
                const result = await this.uploadFile(files.credentials[i], 'credentials', applicationId);
                if (result.success) {
                    uploadedFiles.credentials.push(result.fileData);
                }
            }
        }
        
        // Upload single files
        const singleFileTypes = ['video', 'photo'];
        for (const type of singleFileTypes) {
            if (files[type]) {
                const result = await this.uploadFile(files[type], type, applicationId);
                if (result.success) {
                    uploadedFiles[type] = result.fileData;
                }
            }
        }
        
        return uploadedFiles;
    }
};

export { ApplicationSystem };